import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

const DashboardPage = () => {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [activeServices, setActiveServices] = useState<string[]>(['ebook-writing', 'sales-flyers', 'marketing-ads']); // Give full access to all services

  useEffect(() => {
    const getUser = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        setUser(user);
      } catch (error) {
        console.error('Error getting user:', error);
      } finally {
        setLoading(false);
      }
    };

    getUser();
  }, []);

  const services = [
    {
      id: 'ebook-writing',
      title: 'eBook Writing',
      description: 'Create professional eBooks with AI-powered content generation and 100+ customizable templates',
      price: '$29',
      period: '/month',
      features: [
        '100+ Professional Templates',
        'AI Content Generation',
        'Custom Cover Design',
        'Multi-format Export (PDF, EPUB)',
        'SEO Optimization',
        'Unlimited Projects'
      ],
      icon: 'ri-book-line',
      color: 'blue',
      link: '/dashboard/ai-ebook-generator'
    },
    {
      id: 'sales-flyers',
      title: 'Sales Flyers',
      description: 'Design eye-catching promotional materials and sales flyers with professional templates',
      price: '$19',
      period: '/month',
      features: [
        '50+ Flyer Templates',
        'Drag & Drop Editor',
        'High-Resolution Export',
        'Brand Customization',
        'Print-Ready Formats',
        'Social Media Sizes'
      ],
      icon: 'ri-file-paper-line',
      color: 'green',
      link: '/dashboard/sales-flyers'
    },
    {
      id: 'marketing-ads',
      title: 'Marketing Ads',
      description: 'Create compelling marketing campaigns and advertisements for multiple platforms',
      price: '$39',
      period: '/month',
      features: [
        'Multi-Platform Templates',
        'A/B Testing Tools',
        'Analytics Dashboard',
        'Campaign Management',
        'Social Media Integration',
        'Performance Tracking'
      ],
      icon: 'ri-megaphone-line',
      color: 'purple',
      link: '/dashboard/marketing-ads'
    }
  ];

  const stats = [
    { label: 'Total Projects', value: '24', icon: 'ri-file-list-line', color: 'blue' },
    { label: 'Active Services', value: '3', icon: 'ri-service-line', color: 'green' },
    { label: 'Templates Used', value: '12', icon: 'ri-layout-line', color: 'purple' },
    { label: 'Downloads', value: '156', icon: 'ri-download-line', color: 'orange' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <div className="flex items-center">
                <Link to="/" className="text-2xl font-black text-gray-900 tracking-tight leading-none">
                  AI BOOK AUTHORITY
                </Link>
              </div>
              <nav className="hidden md:flex space-x-8">
                <Link to="/dashboard" className="text-blue-600 font-medium">Dashboard</Link>
                <Link to="/dashboard/ai-ebook-generator" className="text-gray-600 hover:text-blue-600 transition-colors">eBook Generator</Link>
                <Link to="/dashboard/sales-flyers" className="text-gray-600 hover:text-blue-600 transition-colors">Sales Flyers</Link>
                <Link to="/dashboard/marketing-ads" className="text-gray-600 hover:text-blue-600 transition-colors">Marketing Ads</Link>
              </nav>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-600">
                Welcome, {user?.email?.split('@')[0] || 'User'}
              </div>
              <button
                onClick={() => supabase.auth.signOut()}
                className="text-gray-600 hover:text-red-600 transition-colors"
              >
                <i className="ri-logout-line text-lg"></i>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            Welcome to Your Dashboard
          </h2>
          <p className="text-gray-600">
            Manage your content creation services and track your progress
          </p>
        </div>

        {/* Active Services Status */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8">
          <div className="flex items-center mb-4">
            <i className="ri-check-circle-line text-green-600 text-xl mr-3"></i>
            <h3 className="text-lg font-semibold text-green-800">Active Services</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center">
              <i className="ri-book-line text-green-600 mr-2"></i>
              <span className="text-green-700">eBook Writing Service</span>
            </div>
            <div className="flex items-center">
              <i className="ri-file-paper-line text-green-600 mr-2"></i>
              <span className="text-green-700">Sales Flyers Service</span>
            </div>
            <div className="flex items-center">
              <i className="ri-megaphone-line text-green-600 mr-2"></i>
              <span className="text-green-700">Marketing Ads Service</span>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {stats.map((stat, index) => (
            <div key={index} className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">{stat.label}</p>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                </div>
                <div className={`w-12 h-12 bg-${stat.color}-100 rounded-lg flex items-center justify-center`}>
                  <i className={`${stat.icon} text-${stat.color}-600 text-xl`}></i>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Services Section */}
        <div className="mb-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Your Services</h3>
          
          {/* Services Grid - HORIZONTAL LAYOUT */}
          <div className="flex flex-col lg:flex-row gap-6">
            {services.map((service) => (
              <div key={service.id} className="flex-1 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-shadow">
                <div className="p-6 h-full flex flex-col">
                  <div className="flex items-center mb-4">
                    <div className={`w-12 h-12 bg-${service.color}-100 rounded-lg flex items-center justify-center mr-4`}>
                      <i className={`${service.icon} text-${service.color}-600 text-xl`}></i>
                    </div>
                    <div>
                      <h4 className="text-xl font-bold text-gray-900">{service.title}</h4>
                      <div className="flex items-center">
                        <span className="text-2xl font-bold text-gray-900">{service.price}</span>
                        <span className="text-gray-600 ml-1">{service.period}</span>
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-gray-600 mb-6">{service.description}</p>
                  
                  <ul className="space-y-2 mb-6 flex-grow">
                    {service.features.map((feature, index) => (
                      <li key={index} className="flex items-center text-sm text-gray-600">
                        <i className="ri-check-line text-green-500 mr-2"></i>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  
                  <Link
                    to={service.link}
                    className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors text-center block whitespace-nowrap"
                  >
                    Access Service
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Quick Actions</h3>
          <div className="flex flex-col md:flex-row gap-4">
            <Link
              to="/dashboard/ai-ebook-generator"
              className="flex-1 flex items-center p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
            >
              <i className="ri-add-line text-blue-600 text-xl mr-3"></i>
              <span className="font-medium text-blue-700">Create New eBook</span>
            </Link>
            <Link
              to="/dashboard/sales-flyers"
              className="flex-1 flex items-center p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
            >
              <i className="ri-add-line text-green-600 text-xl mr-3"></i>
              <span className="font-medium text-green-700">Design Sales Flyer</span>
            </Link>
            <Link
              to="/dashboard/marketing-ads"
              className="flex-1 flex items-center p-4 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors"
            >
              <i className="ri-add-line text-purple-600 text-xl mr-3"></i>
              <span className="font-medium text-purple-700">Create Marketing Ad</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
